import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { SimulationDashboardPage } from './SimulationDashboardPage';
import { SovereignSplash } from '../../../components/security/SovereignSplash';
import { useAuthStore } from '../../../store/authStore';
import {
  CidadeStep,
  GestorMunicipalStep,
  EscolaStep,
  SimLaunchOverlay,
  type Cidade,
  type Escola,
  type LaunchStep,
  SS,
  criarSessaoSimulacao,
  iniciarSessao,
  buscarEscolasMunicipio,
  sairDaSimulacao,
  isSimUnlocked,
  markSimUnlocked,
  isSimCockpitActive,
  markSimCockpit,
  clearSimFlags,
} from '../../sim-onboarding';
import { getRedirectPath } from '../../../auth/routes';

const LAUNCH_STEPS_TEMPLATE: Omit<LaunchStep, 'status' | 'detail'>[] = [
  { id: 'validar', label: 'Validando escolas selecionadas' },
  { id: 'criar', label: 'Criando sessão de simulação' },
  { id: 'iniciar', label: 'Iniciando sessão no servidor' },
  { id: 'abrir', label: 'Abrindo Gabinete do Prefeito' },
];

function buildLaunchSteps(
  patch: Partial<Record<string, { status: LaunchStep['status']; detail?: string }>>,
): LaunchStep[] {
  return LAUNCH_STEPS_TEMPLATE.map((s) => ({
    ...s,
    status: patch[s.id]?.status ?? 'pending',
    detail: patch[s.id]?.detail,
  }));
}

//#region AC_BLOCK simulation-setup-page -- Wizard de Onboarding integrado ao /dev sob SovereignSplash
// @ac-purpose Permite configurar município, gestor e escolas diretamente pela rota /dev com identidade visual roxa
// @ac-related react-app/src/features/developer-cockpit/pages/SimulationDashboardPage.tsx#simulation-dashboard-page

type Step = 'senha' | 'cidade' | 'gestor' | 'escola' | 'resumo' | 'dashboard';

/** Identidade sintética do prefeito para a senha pública do /simulador */
const SIM_PREFEITO_USER = {
  id: 'sim-studio',
  email: 'sim-studio@internal',
  nome: 'Prefeito (Simulação)',
  perfil: 'SIM_STUDIO',
  cargo: null,
  ativo: true,
};

function cockpitRoleFromUser(user: { perfil?: string; cargo?: string } | null): string {
  if (!user) return 'administrador';
  const perf = (user.perfil || '').toUpperCase();
  const carg = (user.cargo || '').toUpperCase();
  if (perf === 'ADMIN') return 'administrador';
  if (perf === 'SIM_STUDIO' || perf === 'MUNICIPAL' || perf === 'MUNICIPIO') return 'prefeito';
  if (perf === 'ESCOLA') {
    if (carg === 'COORDENACAO' || carg === 'COORDENADOR') return 'coordenador';
    if (carg === 'SECRETARIA') return 'secretaria';
    return 'diretor';
  }
  if (perf === 'PROFESSOR') return 'professor';
  if (perf === 'RESPONSAVEL') return 'responsavel';
  if (perf === 'ALUNO') return 'aluno';
  return 'administrador';
}

function axiosErrorMessage(e: any): string {
  const data = e?.response?.data;
  if (typeof data?.message === 'string') return data.message;
  if (Array.isArray(data?.message)) return data.message.join(' · ');
  if (typeof data?.error === 'string') return data.error;
  if (typeof e?.message === 'string' && e.message !== 'Network Error') return e.message;
  return 'Não foi possível iniciar a simulação. Verifique a conexão e tente novamente.';
}

export function SimulationSetupPage() {
  const { isAuthenticated, user } = useAuthStore();

  const [noSimulationAvailable, setNoSimulationAvailable] = useState(false);
  const [entryChecked, setEntryChecked] = useState(false);

  const [step, setStep] = useState<Step>(() => {
    if (!isSimUnlocked()) return 'senha';
    // Já esteve no cockpit → não flashar o wizard no refresh
    if (isSimCockpitActive()) return 'dashboard';
    return 'cidade';
  });

  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [cidadeSel, setCidadeSel] = useState<Cidade | null>(null);
  const [escolasSel, setEscolasSel] = useState<Set<string>>(new Set());
  const [escolas, setEscolas] = useState<Escola[]>([]);
  const [, setSessionId] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchSteps, setLaunchSteps] = useState<LaunchStep[]>(() => buildLaunchSteps({}));
  const [launchLogs, setLaunchLogs] = useState<string[]>([]);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Entrada:
  // 1) Senha pública → prefeito (wizard cidade/escolas)
  // 2) Já autenticado no /simulador → restaura dashboard se já esteve no cockpit
  // 3) Usuário do app logado → entra no próprio perfil se houver simulação no escopo
  useEffect(() => {
    let cancelled = false;

    const fetchActiveSessions = async (): Promise<any[]> => {
      const token = localStorage.getItem('ac_token') || '';
      const r = await fetch('/api/v1/simulation/sessions/active', {
        credentials: 'include',
        headers: token
          ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
          : { Accept: 'application/json' },
      });
      if (!r.ok) return [];
      const data = await r.json();
      return Array.isArray(data) ? data : (data?.sessions || []);
    };

    const hasLiveSession = (list: any[]) =>
      list.some((s) =>
        ['RUNNING', 'PAUSED', 'PREPARING', 'ACTIVE'].includes(String(s?.status || '').toUpperCase()),
      );

    const goDashboard = () => {
      if (cancelled) return;
      markSimCockpit();
      setNoSimulationAvailable(false);
      setStep('dashboard');
      setEntryChecked(true);
    };

    const resolveEntry = async () => {
      // Prefeito via senha do simulador: se já estava no cockpit, volta pra lá
      if (isSimUnlocked() && localStorage.getItem('ac_token')) {
        if (!localStorage.getItem('ac_user')) {
          localStorage.setItem('ac_user', JSON.stringify(SIM_PREFEITO_USER));
          localStorage.setItem('ac_perfil', 'SIM_STUDIO');
        }

        if (isSimCockpitActive()) {
          goDashboard();
          return;
        }

        try {
          const list = await fetchActiveSessions();
          if (hasLiveSession(list)) {
            goDashboard();
            return;
          }
        } catch {
          // sem API: se tem cidade salva e layout/desktop, assume cockpit
          if (SS.get('sim_cidade') && localStorage.getItem('ac_desktop_layout')) {
            goDashboard();
            return;
          }
        }

        // Autenticado, mas ainda sem sessão criada → retoma wizard
        const rawCidade = SS.get('sim_cidade');
        if (rawCidade) {
          try {
            const c = JSON.parse(rawCidade) as Cidade;
            if (!cancelled) {
              setCidadeSel(c);
              setStep(c.temGestor ? 'escola' : 'gestor');
              setEntryChecked(true);
            }
            return;
          } catch {
            // segue para cidade
          }
        }

        if (!cancelled) {
          setNoSimulationAvailable(false);
          setStep('cidade');
          setEntryChecked(true);
        }
        return;
      }

      if (isAuthenticated && user) {
        const role = cockpitRoleFromUser(user);
        try {
          const list = await fetchActiveSessions();
          const municipioId = (user as any).municipioId as string | undefined;
          const scoped = municipioId
            ? list.filter((s: any) => s.municipioId === municipioId)
            : list;
          const canEnter =
            role === 'administrador' ||
            scoped.length > 0 ||
            (role === 'prefeito' && list.length > 0) ||
            isSimCockpitActive();

          if (canEnter) {
            goDashboard();
            return;
          }
        } catch {
          // fallback abaixo
        }

        if (role === 'administrador') {
          if (!cancelled) {
            setNoSimulationAvailable(false);
            setStep('cidade');
            setEntryChecked(true);
          }
          return;
        }

        if (!cancelled) {
          setNoSimulationAvailable(true);
          setEntryChecked(true);
        }
        return;
      }

      // Desconectado: sempre tela de senha (entrada do prefeito)
      if (!cancelled) {
        setNoSimulationAvailable(false);
        setStep('senha');
        setEntryChecked(true);
      }
    };

    resolveEntry();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (step === 'senha') {
      const t1 = setTimeout(() => inputRef.current?.focus(), 100);
      const t2 = setTimeout(() => inputRef.current?.focus(), 300);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [step]);

  const handleVerify = async () => {
    try {
      const r = await fetch('/api/v1/auth/sim-studio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: senha.trim() }),
      });
      if (!r.ok) throw new Error('invalid');
      const data = await r.json();
      localStorage.setItem('ac_token', data.access_token);
      localStorage.setItem('ac_user', JSON.stringify(SIM_PREFEITO_USER));
      localStorage.setItem('ac_perfil', 'SIM_STUDIO');
      sessionStorage.setItem('ac_auth_method', 'password');
      setSenha('');
      markSimUnlocked();
      setStep('cidade');
    } catch {
      localStorage.removeItem('ac_token');
      localStorage.removeItem('ac_user');
      localStorage.removeItem('ac_perfil');
      clearSimFlags();
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});

      setErro(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleSelectCidade = (c: Cidade) => {
    setCidadeSel(c);
    SS.set('sim_cidade', JSON.stringify(c));
    setStep(c.temGestor ? 'escola' : 'gestor');
    buscarEscolasMunicipio(c.id)
      .then((data) => {
        setEscolas(data);
        setEscolasSel(new Set(data.map((e) => e.id)));
      })
      .catch(() => {});
  };

  const handleGestorCriado = (c: Cidade) => {
    setCidadeSel(c);
    SS.set('sim_cidade', JSON.stringify(c));
    setStep('escola');
    buscarEscolasMunicipio(c.id)
      .then((data) => {
        setEscolas(data);
        setEscolasSel(new Set(data.map((e) => e.id)));
      })
      .catch(() => {});
  };

  const toggleEscola = (e: Escola) => {
    setEscolasSel((prev) => {
      const n = new Set(prev);
      n.has(e.id) ? n.delete(e.id) : n.add(e.id);
      return n;
    });
    setEscolas((prev) => {
      if (prev.some((x) => x.id === e.id)) return prev;
      return [e, ...prev];
    });
  };

  const dismissLaunchError = () => {
    setLaunchError(null);
    setLaunching(false);
    setLaunchLogs([]);
    setLaunchSteps(buildLaunchSteps({}));
  };

  const handleLaunch = async (escolaIdsOverride?: string[]) => {
    if (!cidadeSel || launching) return;
    const ids = escolaIdsOverride?.length
      ? escolaIdsOverride
      : Array.from(escolasSel);
    if (ids.length === 0) {
      toast.error('Selecione ao menos 1 escola para iniciar a simulação.');
      return;
    }
    if (escolaIdsOverride?.length) {
      setEscolasSel(new Set(ids));
    }

    const logs: string[] = [];
    const pushLog = (line: string) => {
      logs.push(line);
      setLaunchLogs([...logs]);
    };
    const setSteps = (
      patch: Partial<Record<string, { status: LaunchStep['status']; detail?: string }>>,
    ) => setLaunchSteps(buildLaunchSteps(patch));

    setLaunching(true);
    setLaunchError(null);
    setLaunchLogs([]);
    setSteps({ validar: { status: 'active', detail: `${ids.length} escola(s) em ${cidadeSel.nome}` } });
    pushLog(`⚡ Validando ${ids.length} escola(s) em ${cidadeSel.nome}/${cidadeSel.uf}…`);

    try {
      await new Promise((r) => setTimeout(r, 280));
      setSteps({
        validar: { status: 'done', detail: `${ids.length} escola(s) prontas` },
        criar: { status: 'active', detail: 'POST /simulation/sessions' },
      });
      pushLog('✅ Seleção validada');
      pushLog('📡 Criando sessão de simulação…');

      const session = await criarSessaoSimulacao({
        municipioId: cidadeSel.id,
        escolaIds: ids,
      });
      setSessionId(session.id);
      markSimCockpit(session.id);
      pushLog(`✅ Sessão criada: ${session.id.slice(0, 8)}…`);

      setSteps({
        validar: { status: 'done' },
        criar: { status: 'done', detail: `id ${session.id.slice(0, 8)}…` },
        iniciar: { status: 'active', detail: 'POST /simulation/sessions/:id/start' },
      });
      pushLog('🚀 Iniciando sessão no servidor…');

      try {
        await iniciarSessao(session.id);
        pushLog('✅ Sessão RUNNING');
        setSteps({
          validar: { status: 'done' },
          criar: { status: 'done' },
          iniciar: { status: 'done' },
          abrir: { status: 'active', detail: 'Carregando cockpit…' },
        });
      } catch (startErr: any) {
        console.warn('[SimSetup] Sessão criada, mas start falhou:', startErr);
        const msg =
          axiosErrorMessage(startErr) ||
          'Sessão criada. Inicie pelo painel se ainda estiver em preparação.';
        pushLog(`⚠️ Start parcial: ${msg}`);
        toast.warning(msg);
        setSteps({
          validar: { status: 'done' },
          criar: { status: 'done' },
          iniciar: { status: 'done', detail: 'Criada — start pode ser manual' },
          abrir: { status: 'active', detail: 'Abrindo painel mesmo assim…' },
        });
      }

      pushLog('🏛️ Abrindo Gabinete do Prefeito…');
      await new Promise((r) => setTimeout(r, 400));
      setSteps({
        validar: { status: 'done' },
        criar: { status: 'done' },
        iniciar: { status: 'done' },
        abrir: { status: 'done' },
      });
      pushLog('✅ Pronto');
      setStep('dashboard');
      setLaunching(false);
    } catch (e: any) {
      console.error('[SimSetup] Erro ao criar sessão:', e);
      const msg = axiosErrorMessage(e);
      pushLog(`❌ ${msg}`);
      setLaunchError(msg);
      setLaunchSteps((prev) =>
        prev.map((s) =>
          s.status === 'active' ? { ...s, status: 'error' as const, detail: msg } : s,
        ),
      );
      toast.error(msg);
      // Mantém overlay aberto com botão "Fechar" — não limpa launching no finally
    }
  };

  if (!entryChecked && step !== 'senha') {
    return (
      <div className="w-screen h-screen overflow-hidden text-[#cdd6f4] flex items-center justify-center bg-[#0b0928]">
        <span className="text-sm opacity-70">Carregando simulador…</span>
      </div>
    );
  }

  if (step === 'dashboard') {
    return <SimulationDashboardPage />;
  }

  const isWide = step === 'escola';

  return (
    <div className="w-screen h-screen overflow-hidden text-[#cdd6f4] select-none relative">
      <SovereignSplash
        skipIntro
        // No passo do mapa: conteúdo full-bleed por cima da marca (logo fica atrás)
        alignLogoRight={isWide}
        splitPct={isWide ? 0 : 42}
        titleWord1="Aluno"
        titleWord2="Conectado"
        subtitle="Central de Simulação em Tempo Real"
        onBackClick={() => { void sairDaSimulacao('/login'); }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isWide ? 'stretch' : 'center',
            justifyContent: isWide ? 'stretch' : 'center',
            height: '100%',
            width: '100%',
            // Margem ~30px da tela — mapa o maior possível para mostrar dimensão real
            padding: isWide ? 30 : '40px 20px',
            boxSizing: 'border-box',
            overflowY: isWide ? 'hidden' : 'auto',
          }}
        >
          {noSimulationAvailable && (
            <div className="w-full max-w-md bg-[#0d0a21]/95 border-2 border-[#7c3aed]/40 rounded-3xl p-10 text-center shadow-xl">
              <div className="text-4xl mb-4 opacity-40">📡</div>
              <h2 className="text-lg font-bold text-white mb-2">Simulação Inativa</h2>
              <p className="text-sm text-[#94a3b8] leading-relaxed">
                Você está logado, mas não há sessão ativa no seu município/escola.
                Use a senha do prefeito (limpa a sessão atual) ou volte ao seu painel.
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => {
                    // Mesma limpeza do cadeado "Sair da simulação" → recarrega /simulador desconectado → senha
                    void sairDaSimulacao('/simulador');
                  }}
                  className="w-full px-5 py-2.5 rounded-xl text-white text-xs font-bold border-none cursor-pointer"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}
                >
                  Sair e entrar com senha do prefeito
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Volta ao painel do perfil logado (não limpa login do app)
                    const path = getRedirectPath(user?.perfil || '', user?.cargo || undefined);
                    window.location.href = path || '/login';
                  }}
                  className="w-full px-5 py-2.5 rounded-xl bg-white/5 text-[#94a3b8] hover:text-white text-xs font-bold border border-white/10 cursor-pointer"
                >
                  Voltar ao meu painel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Mesma limpeza do cadeado → login limpo (sem loop)
                    void sairDaSimulacao('/login');
                  }}
                  className="w-full px-5 py-2 rounded-xl bg-transparent text-[#64748b] hover:text-[#f87171] text-[10px] font-bold border-none cursor-pointer"
                >
                  Sair da simulação / encerrar sessão
                </button>
              </div>
            </div>
          )}

          {!noSimulationAvailable && step === 'senha' && (
            <div
              className={`w-full max-w-sm bg-[#0d0a21]/95 border-2 border-[#7c3aed]/50 rounded-3xl p-10 shadow-[0_25px_60px_-15px_rgba(124,58,237,0.5)] backdrop-blur-xl ${shake ? 'shake-box' : ''}`}
            >
              <div className="text-center mb-6">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#a78bfa] mb-2">
                  Acesso Prefeito
                </div>
                <h2 className="text-xl font-black text-white m-0">Área Restrita</h2>
                <p className="text-[12px] text-[#94a3b8] mt-2 leading-relaxed m-0">
                  Digite a senha fornecida pelo gerente para liberar a simulação da sua cidade.
                </p>
              </div>
              <input
                ref={inputRef}
                type="password"
                placeholder="Digite a senha..."
                value={senha}
                onChange={(e) => {
                  setSenha(e.target.value);
                  setErro(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleVerify();
                }}
                className="w-full h-11 px-4 rounded-xl bg-[#11111b] border border-[#313244] text-white text-sm outline-none focus:border-[#a78bfa]"
              />
              {erro && (
                <p className="text-[#f87171] text-[11px] mt-2 mb-0">Senha inválida. Tente novamente.</p>
              )}
              <button
                type="button"
                onClick={handleVerify}
                className="w-full mt-4 h-11 rounded-xl border-none cursor-pointer font-bold text-sm text-white"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}
              >
                Entrar
              </button>
            </div>
          )}

          {step === 'cidade' && (
            <div className="w-full max-w-2xl bg-[#0d0a21]/95 border-2 border-[#7c3aed]/50 rounded-3xl p-10 shadow-[0_25px_60px_-15px_rgba(124,58,237,0.5)] backdrop-blur-xl animate-fadeIn pointer-events-auto">
              <CidadeStep
                selecionada={cidadeSel}
                onSelect={handleSelectCidade}
                onClear={() => {
                  setCidadeSel(null);
                  SS.del('sim_cidade');
                }}
                onNext={() => setStep(cidadeSel?.temGestor ? 'escola' : 'gestor')}
              />
            </div>
          )}

          {step === 'gestor' && cidadeSel && (
            <div className="w-full bg-[#0d0a21]/95 border-2 border-[#7c3aed]/50 rounded-3xl p-10 shadow-[0_25px_60px_-15px_rgba(124,58,237,0.5)] backdrop-blur-xl animate-fadeIn pointer-events-auto">
              <GestorMunicipalStep
                cidade={cidadeSel}
                onGestorCriado={handleGestorCriado}
                onNext={() => setStep('escola')}
              />
            </div>
          )}

          {step === 'escola' && cidadeSel && (
            <div
              className="w-full h-full min-h-0 bg-[#0d0a21]/95 border-2 border-[#7c3aed]/50 rounded-3xl p-3 shadow-[0_25px_60px_-15px_rgba(124,58,237,0.5)] backdrop-blur-xl animate-fadeIn pointer-events-auto flex flex-col"
              style={{ maxWidth: 'none' }}
            >
              <EscolaStep
                cidade={cidadeSel}
                selecionados={escolasSel}
                onToggle={toggleEscola}
                onNext={(ids) => { void handleLaunch(ids); }}
                launching={launching}
              />
            </div>
          )}
        </div>
      </SovereignSplash>

      <SimLaunchOverlay
        open={launching}
        steps={launchSteps}
        logLines={launchLogs}
        error={launchError}
        onDismissError={launchError ? dismissLaunchError : undefined}
        subtitle={
          cidadeSel
            ? `Montando a sessão de ${cidadeSel.nome}/${cidadeSel.uf} e abrindo o cockpit.`
            : undefined
        }
      />

      <style>{`
        .shake-box { animation: shake-ani 0.4s ease-in-out; }
        @keyframes shake-ani {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
      `}</style>
    </div>
  );
}
//#endregion AC_BLOCK simulation-setup-page
